import { useCallback, useEffect, useRef, useState } from 'react';
import type { RawLocation } from '../lib/attendance-readiness';

/**
 * Pembacaan lokasi perangkat.
 *
 * Tiga keadaan yang WAJIB dirancang, bukan ditemukan saat demo (UI-PAGE-MAP §5):
 *
 * - **Izin ditolak** → jelaskan cara mengaktifkan. Jangan berputar tanpa akhir.
 * - **GPS tidak tersedia / timeout** → berhenti dengan jujur, biarkan pengguna memilih
 *   melanjutkan tanpa lokasi bila kebijakan mengizinkan.
 * - **Akurasi buruk** → tetap dilaporkan apa adanya; yang menilai adalah kebijakan, bukan hook ini.
 *
 * Hook ini TIDAK menilai apakah lokasi cukup baik. Ia hanya melaporkan apa yang dibaca perangkat.
 * Penilaian ada di `attendance-readiness.ts`, dan keputusan final ada di server.
 */

export type GeoStatus = 'idle' | 'locating' | 'ready' | 'denied' | 'unavailable' | 'timeout';

export interface GeoState {
  readonly status: GeoStatus;
  readonly location: RawLocation;
  /** Waktu pembacaan terakhir — supaya pengguna tahu angkanya basi atau baru. */
  readonly readAt: Date | null;
}

const NO_LOCATION: RawLocation = { latitude: null, longitude: null, accuracyMeters: null };

const OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  // 20 detik: cukup bagi GPS dingin di dalam gedung, tetapi tidak membuat kurir menunggu
  // tanpa kepastian. Setelah itu ia diberi pilihan, bukan spinner.
  timeout: 20_000,
  maximumAge: 0,
};

export function useGeolocation(): GeoState & { readonly retry: () => void } {
  const [state, setState] = useState<GeoState>({
    status: 'idle',
    location: NO_LOCATION,
    readAt: null,
  });
  const watchId = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (watchId.current !== null && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState({ status: 'unavailable', location: NO_LOCATION, readAt: null });
      return;
    }

    setState({ status: 'locating', location: NO_LOCATION, readAt: null });
    stop();

    // `watchPosition`, bukan `getCurrentPosition`: pembacaan pertama sering berakurasi ratusan
    // meter lalu membaik dalam beberapa detik. Menampilkan yang pertama saja akan memblokir
    // check-in yang sebenarnya sah beberapa saat kemudian.
    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        setState({
          status: 'ready',
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyMeters: position.coords.accuracy,
          },
          readAt: new Date(),
        });
      },
      (error) => {
        stop();
        const status: GeoStatus =
          error.code === error.PERMISSION_DENIED
            ? 'denied'
            : error.code === error.TIMEOUT
              ? 'timeout'
              : 'unavailable';
        setState({ status, location: NO_LOCATION, readAt: null });
      },
      OPTIONS,
    );
  }, [stop]);

  useEffect(() => {
    start();
    return stop;
  }, [start, stop]);

  return { ...state, retry: start };
}

/** Penjelasan keadaan lokasi — kalimat yang dapat ditindaklanjuti, bukan kode galat. */
export function describeGeoStatus(status: GeoStatus): { title: string; hint: string } {
  switch (status) {
    case 'locating':
      return {
        title: 'Mencari lokasi…',
        hint: 'Berdiri di area terbuka mempercepat pembacaan.',
      };
    case 'ready':
      return { title: 'Lokasi terbaca', hint: '' };
    case 'denied':
      return {
        title: 'Izin lokasi ditolak',
        hint: 'Buka pengaturan situs di browser Anda, aktifkan izin Lokasi untuk halaman ini, lalu tekan "Coba lagi". Anda tetap dapat melanjutkan tanpa lokasi bila kebijakan organisasi mengizinkan.',
      };
    case 'timeout':
      return {
        title: 'Lokasi belum terbaca',
        hint: 'Sinyal GPS belum didapat. Pindah ke area terbuka lalu coba lagi, atau lanjutkan tanpa lokasi bila kebijakan mengizinkan.',
      };
    case 'unavailable':
      return {
        title: 'Lokasi tidak tersedia',
        hint: 'Perangkat atau browser ini tidak menyediakan lokasi. Anda tetap dapat melanjutkan bila kebijakan organisasi mengizinkan.',
      };
    case 'idle':
    default:
      return { title: 'Lokasi belum diminta', hint: '' };
  }
}
